import { apiClient } from '../lib/apiClient';

// Types for Smart Read
export interface Article {
  id: string;
  user_id: string;
  title?: string; // Added to match backend
  topic: string;
  length: string;
  content: string;
  language: string;
  difficulty?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export interface ArticleTranslation {
  id: string;
  article_id: string;
  language: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface ArticleWordTranslation {
  word: string;  // Primary key is now just the word
  en: string | null;
  he: string | null;
  nl: string | null;
  article_language: string; // The source language
  created_at: string;
  updated_at: string;
}

// Junction table to associate words with articles
export interface ArticleWord {
  id: string;
  article_id: string;
  word: string;
  created_at: string;
}

export interface GenerateArticleParams {
  topic: string;
  length: 'short' | 'long';
  language: string;
  nativeLanguage?: string;
}

export interface GenerateArticleResponse {
  article: {
    language: string;
    length: string;
    topic: string;
    content: string;
  };
  translations: Record<string, string>;
  word_translations: Array<{
    word: string;
    [key: string]: string;
  }>;
}

// Generate an article using the AI Gateway (via apiClient)
export async function generateArticle(params: GenerateArticleParams): Promise<string> {
  const result = await apiClient.generateArticle(params);
  // apiClient.generateArticle returns the created article object
  // We return the ID to match the original signature
  return result.id;
}

// Fetch a list of all articles
export async function getArticles() {
  const articles = await apiClient.getArticles();
  // Map backend camelCase to frontend snake_case if necessary
  // But our types define snake_case. Let's check backend response.
  // Backend returns objects with camelCase keys usually if strictly following TS, 
  // but Drizzle/SQLite might return snake_case if configured or if columns are snake_case.
  // In words/sentences routes, I mapped manually sometimes.
  // In smart-read.ts, I return `...a` where `a` is from `db.select().from(articles)`.
  // Drizzle schema defines columns with snake_case names but property names are camelCase (e.g. `userId: text('user_id')`).
  // Drizzle query results use the property names (camelCase).
  // So `userId` will be `userId` in the JSON.
  // But `Article` interface here expects `user_id`.
  // I should map the response.

  return articles.map(mapArticleFromBackend);
}

// Fetch a single article with its translations and word translations
export async function getArticleById(id: string) {
  const data = await apiClient.getArticle(id);
  
  return {
    article: mapArticleFromBackend(data.article),
    translations: data.translations.map(mapTranslationFromBackend),
    wordTranslations: data.wordTranslations.map(mapWordTranslationFromBackend)
  };
}

// Delete an article
export async function deleteArticle(id: string) {
  await apiClient.deleteArticle(id);
  return true;
}

// Search articles
export async function searchArticles({ query, language }: { query?: string, language?: string }) {
  const articles = await apiClient.searchArticles({ query, language });
  return articles.map(mapArticleFromBackend);
}

// Save a word from an article to user's collection
export async function saveWordToCollection(word: string, language: string) {
  return await apiClient.saveWordToCollection(word, language);
}

// Mark a word as known in user's collection
export async function markWordAsKnown(wordId: string) {
  return await apiClient.markWordAsKnown(wordId);
}

export async function getWordTranslation(word: string, sourceLanguage: string): Promise<ArticleWordTranslation> {
  // Try to get translation from browser cache first
  const cacheKey = `word-translation:${word}:${sourceLanguage}`;
  const cached = sessionStorage.getItem(cacheKey);
  if (cached) {
    return JSON.parse(cached);
  }

  // Get from API
  try {
    const translation = await apiClient.getWordTranslation(word);
    
    // Map and cache
    const mapped = mapWordTranslationFromBackend(translation);
    sessionStorage.setItem(cacheKey, JSON.stringify(mapped));
    return mapped;
  } catch (error) {
    console.error('Error fetching word translation:', error);
    // Return empty/partial if failed
    return {
        word,
        en: null,
        he: null,
        nl: null,
        article_language: sourceLanguage,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
    };
  }
}

// Helper mappers
function mapArticleFromBackend(a: any): Article {
  return {
    id: a.id,
    user_id: a.userId,
    title: a.title,
    topic: a.title, // Map title to topic as fallback/alias
    length: a.metadata?.length || 'short',
    content: a.content,
    language: a.language,
    difficulty: a.difficulty,
    metadata: a.metadata,
    created_at: a.createdAt,
    updated_at: a.updatedAt
  };
}

function mapTranslationFromBackend(t: any): ArticleTranslation {
  return {
    id: t.id,
    article_id: t.articleId,
    language: t.language,
    content: t.content,
    created_at: t.createdAt,
    updated_at: t.updatedAt
  };
}

function mapWordTranslationFromBackend(wt: any): ArticleWordTranslation {
  return {
    word: wt.word,
    en: wt.en,
    he: wt.he,
    nl: wt.nl,
    article_language: wt.articleLanguage,
    created_at: wt.createdAt,
    updated_at: wt.updatedAt
  };
}
